import { isEmpty } from 'lodash';

import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
} from 'js/constants/connectionCategories';
import { hasAccessRole, isReadOnly } from 'js/lib/ACLUtils';
import { isSharedDataset } from 'js/lib/URLUtils';
import flowTypes from 'new/components/flows/types/flowTypes';
import nexsetTypes from 'new/constants/nexsetTypes';

import { NEXSET_TAB_IDS, type NexsetTabId } from './tabIds';

export interface NexsetTabContext {
  data: any;
  flowType?: string;
  dataSource?: any;
  parentDataSet?: any;
  getMetaInfoById?: (id: string) => any;
  connectionCategory?: string;
  nodeType?: string;
}

// ============================================================================
// DECLARATIVE TAB VISIBILITY RULES
// ============================================================================

/**
 * All available tabs in their display order.
 * This serves as the default set when no specific rules apply.
 */
const ALL_TABS: NexsetTabId[] = [
  NEXSET_TAB_IDS.OVERVIEW,
  NEXSET_TAB_IDS.SCHEMA,
  NEXSET_TAB_IDS.RULES,
  NEXSET_TAB_IDS.STATS,
  NEXSET_TAB_IDS.NOTIFICATIONS,
  NEXSET_TAB_IDS.ACTIVITY,
  NEXSET_TAB_IDS.ERROR_DATA,
  NEXSET_TAB_IDS.SHARE,
];

/**
 * Tab visibility rules by nexset type.
 * Each rule specifies which tabs to INCLUDE for a given nexset type.
 * Using 'include' makes it immediately clear what tabs are visible.
 *
 * @example
 * // To add a new nexset type with custom tabs:
 * [nexsetTypes.my_new_type]: {
 *   tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY]
 * }
 *
 * @example
 * // To modify tabs for splitter_output (e.g., remove STATS):
 * [nexsetTypes.splitter_output]: {
 *   tabs: [
 *     NEXSET_TAB_IDS.OVERVIEW,
 *     // NEXSET_TAB_IDS.STATS,  // <-- Comment out to hide
 *     NEXSET_TAB_IDS.NOTIFICATIONS,
 *     NEXSET_TAB_IDS.ACTIVITY,
 *     NEXSET_TAB_IDS.ERROR_DATA
 *   ]
 * }
 */
const NEXSET_TYPE_TAB_RULES: Record<string, { tabs: NexsetTabId[] }> = {
  // Splitter output shows most tabs except schema, rules, and share
  [nexsetTypes.splitter_output]: {
    tabs: [
      NEXSET_TAB_IDS.OVERVIEW,
      NEXSET_TAB_IDS.STATS,
      NEXSET_TAB_IDS.NOTIFICATIONS,
      NEXSET_TAB_IDS.ACTIVITY,
      NEXSET_TAB_IDS.ERROR_DATA,
    ],
  },

  // Basic operational nodes show minimal information
  [nexsetTypes.splitter]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.api_endpoint]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.api_result]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.api_error]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.rag_retriever]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.rag_reranker]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.rag_enricher]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },
  [nexsetTypes.rag_query_rewriter]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY],
  },

  // API modifier includes rules in addition to basic operational tabs
  [nexsetTypes.api_modifier]: {
    tabs: [NEXSET_TAB_IDS.OVERVIEW, NEXSET_TAB_IDS.ACTIVITY, NEXSET_TAB_IDS.RULES],
  },
};

/**
 * Type alias for conditional rule functions.
 * These functions receive the full context and return tabs to exclude.
 */
type ConditionalRule = (context: NexsetTabContext) => NexsetTabId[];

/**
 * Conditional rules that apply based on context beyond just nexset type.
 * These are evaluated after type-specific rules.
 *
 * Each function receives the full context and returns an array of tabs to EXCLUDE.
 * They are checked in order, and all matching exclusions are combined.
 *
 * @example
 * // To add a new conditional rule:
 * const hideSchemaForLargeDatasets: ConditionalRule = (context) => {
 *   if (context.data?.record_count > 1000000) {
 *     return [NEXSET_TAB_IDS.SCHEMA];
 *   }
 *   return [];
 * };
 *
 * Then add to CONDITIONAL_EXCLUSION_RULES array.
 */
const CONDITIONAL_EXCLUSION_RULES: ConditionalRule[] = [
  /**
   * Hide Share tab for replication flows with children in file/database connections.
   * This prevents sharing intermediate nexsets in replication pipelines.
   */
  (context: NexsetTabContext): NexsetTabId[] => {
    const { data, flowType, dataSource, getMetaInfoById } = context;
    const actualFlowType = flowType || data?.flow_type;

    if (actualFlowType === flowTypes.replication && dataSource && getMetaInfoById) {
      const connectionCategory = getMetaInfoById(dataSource?.source_type)?.connectionCategory;

      if (
        [CONNECTION_CATEGORY_FILE, CONNECTION_CATEGORY_DATABASE].includes(connectionCategory) &&
        !isEmpty(data?.children)
      ) {
        return [NEXSET_TAB_IDS.SHARE];
      }
    }

    return [];
  },
];

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Gets the list of allowed tab IDs for a nexset based on the provided context.
 *
 * This function uses a declarative rule system that makes it easy to:
 * - See what tabs each nexset type shows at a glance
 * - Add new nexset types with custom tab visibility
 * - Add conditional rules based on context
 * - Maintain and understand the tab visibility logic
 *
 * **Rule Evaluation Order:**
 * 1. Special case rules (e.g., shared nexsets)
 * 2. Nexset type-specific rules (NEXSET_TYPE_TAB_RULES)
 * 3. Conditional exclusion rules (CONDITIONAL_EXCLUSION_RULES)
 * 4. Default: all tabs
 *
 * @param context - Context information about the nexset
 * @returns Array of tab IDs that should be shown, in the order they should appear
 *
 * @example
 * // Get tabs for a splitter node
 * const tabs = getAllowedNexsetTabs({
 *   data: { nexset_type: 'splitter' }
 * });
 * // Returns: ['overview', 'activity']
 *
 * @example
 * // Get tabs for a shared nexset
 * const tabs = getAllowedNexsetTabs({
 *   data: { dataSetType: 'shared' }
 * });
 * // Returns: ['overview', 'schema']
 */
export function getAllowedNexsetTabs(context: NexsetTabContext): NexsetTabId[] {
  const { data } = context;
  const nexsetType = data?.nexset_type;

  if (isSharedDataset(data)) {
    const baseTabs: NexsetTabId[] = [
      NEXSET_TAB_IDS.OVERVIEW,
      NEXSET_TAB_IDS.RULES,
      NEXSET_TAB_IDS.STATS,
      NEXSET_TAB_IDS.ACTIVITY,
      NEXSET_TAB_IDS.ERROR_DATA,
    ];

    // // Add SHARE tab only for admin or owner
    // if (hasAccessRole(data, 'admin') || hasAccessRole(data, 'owner')) {
    //   return [...baseTabs, NEXSET_TAB_IDS.SHARE];
    // }

    return baseTabs;
  }

  // 1. Check for nexset type-specific rules
  if (nexsetType && NEXSET_TYPE_TAB_RULES[nexsetType]) {
    return NEXSET_TYPE_TAB_RULES[nexsetType].tabs;
  }

  // 2. Start with all tabs and apply conditional exclusions
  let allowedTabs = [...ALL_TABS];

  for (const rule of CONDITIONAL_EXCLUSION_RULES) {
    const tabsToExclude = rule(context);
    if (tabsToExclude.length > 0) {
      allowedTabs = allowedTabs.filter((tab) => !tabsToExclude.includes(tab));
    }
  }

  // 4. Return filtered tabs (or all tabs if no exclusions applied)
  return allowedTabs;
}
